import {
  ApplicationConfig,
  importProvidersFrom,
  APP_INITIALIZER,
  LOCALE_ID,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, withPreloading, withInMemoryScrolling } from "@angular/router";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideDotLottie } from 'ngx-lottie/dotlottie-web';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
  HTTP_INTERCEPTORS,
  HttpClient,
} from "@angular/common/http";
import { FlexLayoutModule } from "@ngbracket/ngx-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import {
  MatDialogModule,
  MAT_DIALOG_DEFAULT_OPTIONS,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatIconModule } from "@angular/material/icon";
import { MatMomentDateModule } from "@angular/material-moment-adapter";
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from "@ngx-translate/core";
import { provideNgxMask } from "ngx-mask";
import { registerLocaleData, DecimalPipe } from "@angular/common";
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from "@angular/material/form-field";
import { RouteReuseStrategy } from "@angular/router";

import { routes } from "./app.routes";
import { PreloadStrategy } from "~/core/helpers/preload-strategy";
import { CustomReuseStrategy } from "~/core/helpers/route-reuse.strategy";
import { HttpLoaderFactory } from "src/app/core/helpers/translate-loader-factory";
import { UnauthorizedInterceptor } from "./core/helpers/unauthorizedInterceptor";
import { TokenInterceptor } from "./core/helpers/tokenInterceptor";
import { KycInterceptor } from "~/core/helpers/kyc-interceptor.service";
import { MfaInterceptor } from "~/core/helpers/mfa.interceptor";
import { ErrorInterceptor } from "~/core/helpers/error-interceptor.service";
import { versionInterceptor } from "~/core/helpers/version.interceptor";
import { LocaleService } from "~/core/services/locale.service";
import { CustomDateFormat } from "./components/date-picker/custom-date-format";
import { StorageService } from "~/core/services/storage.service";
import { UtilsService } from "~/core/helpers/utils";
import { PaginatorIntlService } from "~/core/services/paginator-intl.service";
import { initializeStorageApp } from "./core/initializers/app-storage.initializer";
import { IndexedDBService } from "~/core/services/indexed-db-storage.service";
import { CompanyService } from "~/core/services/company.service";
import { CookiePermissionService } from "~/core/services/cookie-permission.service";
import { PermissionService } from "~/pages/back-office/roles/services/permission.service";
import { AuthenticationService } from "~/core/services/authentication.service";
import { FormatOddService } from "./core/services/format-odd.service";
import { MenuTopBarBasicMobilePlus2Module } from "~/containers/menu-top-bar-basic-mobile-plus2/menu-top-bar-basic-mobile-plus2.module";
import { ImgLoadingModule } from "~/components/img-loading/img-loading.module";
import { ColorPickerModule } from "ngx-color-picker";

import localePt from "@angular/common/locales/pt";
import localeEn from "@angular/common/locales/en";
import localeEs from "@angular/common/locales/es";
import localeFr from "@angular/common/locales/fr";
import localeDO from "@angular/common/locales/es-DO";

registerLocaleData(localePt);
registerLocaleData(localeEn);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeDO);

const matFormFieldDefaults: MatFormFieldDefaultOptions = {
  appearance: "outline",
  floatLabel: "always",
  hideRequiredMarker: false,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withPreloading(PreloadStrategy),
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" }),
    ),
    provideAnimations(),
    provideDotLottie({ player: () => import('@lottiefiles/dotlottie-web').then(m => m.DotLottie) }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([versionInterceptor]),
    ),
    provideNgxMask(),
    importProvidersFrom(
      FlexLayoutModule,
      FormsModule,
      ReactiveFormsModule,
      ScrollingModule,
      MatSnackBarModule,
      MatDialogModule,
      MatButtonModule,
      MatSidenavModule,
      MatIconModule,
      MatMomentDateModule,
      MenuTopBarBasicMobilePlus2Module,
      ImgLoadingModule,
      ColorPickerModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeService: LocaleService) => localeService.getLocale(),
      deps: [LocaleService],
    },
    { provide: MAT_DATE_FORMATS, useClass: CustomDateFormat },
    {
      provide: LOCALE_ID,
      useFactory: (storageService: StorageService) =>
        storageService.getLanguage() || UtilsService.getBrowserLanguage(),
      deps: [StorageService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KycInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MfaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: MatPaginatorIntl,
      useFactory: (translate: TranslateService) => {
        const service = new PaginatorIntlService();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeStorageApp,
      deps: [
        IndexedDBService,
        StorageService,
        CompanyService,
        CookiePermissionService,
        PermissionService,
        AuthenticationService,
      ],
      multi: true,
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: matFormFieldDefaults },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        enterAnimationDuration: "0ms",
        exitAnimationDuration: "0ms",
      },
    },
    DecimalPipe,
    FormatOddService,
  ],
};
